import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  FileText,
  PackageSearch,
  Search,
  UserCircle2,
  Filter,
  Eye,
  X,
  Check,
  RotateCcw,
  Activity,
  ShieldCheck,
  Calendar
} from "lucide-react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

const Logs = () => {
  const [activeTab, setActiveTab] = useState("Obat");
  const [logs, setLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    employee_id: "",
    type: "",
    role: "",
    search: "",
    sort: "",
    per_page: 10,
  });
  const [page, setPage] = useState(1);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [selectedLogDetail, setSelectedLogDetail] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const activeFilterCount = [filters.employee_id, filters.role, filters.type].filter(Boolean).length;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { employee_id, type, role, search, sort, per_page } = filters;
      const params = {
        ...(employee_id && { employee_id }),
        ...(type && { type }),
        ...(role && { role }),
        ...(search && { search }),
        ...(sort && { sort }),
        per_page,
        page,
      };

      const url =
        activeTab === "Obat"
          ? `${process.env.REACT_APP_API_BASE_URL}/v1/logs/obat`
          : `${process.env.REACT_APP_API_BASE_URL}/v1/logs`;

      const res = await axios.get(url, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setLogs(res.data.data || []);
      setTotalPages(res.data.meta?.last_page || 1);
    } catch (err) {
      console.error("Gagal fetch logs:", err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/v1/employee`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployees(res.data.data || []);
    } catch (err) {
      console.error("Gagal fetch employee:", err);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filters, activeTab, page]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilters((prev) => ({ ...prev, search: value }));
    setPage(1);
  };

  const handleFilterApply = () => {
    setShowFilterModal(false);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const resetFilter = () => {
    setFilters((prev) => ({
      ...prev,
      employee_id: "",
      type: "",
      role: "",
    }));
    setPage(1);
    setShowFilterModal(false);
  };

  return (
    <div className="space-y-6 pb-12">
      {/* 1. Header Bar */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4"
      >
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-50 rounded-2xl border border-emerald-100 text-emerald-600">
            <Activity className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              System Audit Logs
            </h1>
            <p className="text-xs text-gray-500">
              Jejak riwayat aktivitas transaksi, mutasi obat, dan perubahan data sistem apotek
            </p>
          </div>
        </div>

        {/* Tab Selection */}
        <div className="p-1 bg-gray-100 rounded-2xl flex items-center gap-1 text-xs">
          <button
            className={`px-4 py-2 rounded-xl font-bold transition-all flex items-center gap-1.5 ${
              activeTab === "Obat"
                ? "bg-white text-emerald-700 shadow-sm font-extrabold"
                : "text-gray-500 hover:text-gray-900"
            }`}
            onClick={() => {
              setActiveTab("Obat");
              setPage(1);
            }}
          >
            <PackageSearch className="w-3.5 h-3.5" />
            <span>Log Obat & Stok</span>
          </button>

          <button
            className={`px-4 py-2 rounded-xl font-bold transition-all flex items-center gap-1.5 ${
              activeTab === "Other"
                ? "bg-white text-emerald-700 shadow-sm font-extrabold"
                : "text-gray-500 hover:text-gray-900"
            }`}
            onClick={() => {
              setActiveTab("Other");
              setPage(1);
            }}
          >
            <FileText className="w-3.5 h-3.5" />
            <span>Log Aktivitas Sistem</span>
          </button>
        </div>
      </motion.div>

      {/* 2. Metric Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-5"
      >
        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Kategori Log Aktif
            </p>
            <h2 className="text-xl font-extrabold text-emerald-700 font-mono">
              Log {activeTab}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <Activity className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Log Ditemukan
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 font-mono">
              {logs.length}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-teal-50 text-teal-600 border border-teal-100">
            <FileText className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Filter Aktif
            </p>
            <h2 className="text-3xl font-extrabold text-emerald-600 font-mono">
              {activeFilterCount}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <ShieldCheck className="w-6 h-6" />
          </div>
        </div>
      </motion.div>

      {/* 3. Control & Table Panel */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Cari pesan atau ref log..."
              value={filters.search}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
            />
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto justify-end">
            {activeFilterCount > 0 && (
              <span className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full">
                {activeFilterCount} Filter Diterapkan
              </span>
            )}

            <button
              onClick={() => setShowFilterModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-2xl transition-all shadow-sm"
            >
              <Filter className="w-3.5 h-3.5 text-emerald-600" />
              <span>Filter Audit</span>
            </button>
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-left text-xs text-gray-600">
              <thead>
                <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                  {activeTab === "Obat" && <th className="p-3.5">Obat Master</th>}
                  <th className="p-3.5">Kode Referensi</th>
                  <th className="p-3.5">Aktivitas / Pesan Log</th>
                  <th className="p-3.5">Waktu Log</th>
                  <th className="p-3.5">Role Aktor</th>
                  <th className="p-3.5">Karyawan / Aktor</th>
                  <th className="p-3.5 text-center">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.length === 0 ? (
                  <tr>
                    <td
                      colSpan={activeTab === "Obat" ? 7 : 6}
                      className="p-12 text-center text-gray-400 font-semibold"
                    >
                      Belum ada data log aktivitas ditemukan.
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-emerald-50/20 transition-colors">
                      {activeTab === "Obat" && (
                        <td className="p-3.5 font-bold text-gray-900">
                          {log.obat?.name || "-"}
                        </td>
                      )}
                      <td className="p-3.5 font-mono font-extrabold text-gray-800">
                        <span className="px-2.5 py-1 rounded-full bg-gray-100 border border-gray-200 text-[10px]">
                          {log.ref?.code || log.code || "-"}
                        </span>
                      </td>
                      <td className="p-3.5 font-semibold text-gray-800">
                        {log.message || "-"}
                      </td>
                      <td className="p-3.5 font-mono text-gray-600">
                        {new Date(log.log_at || log.created_at).toLocaleString("id-ID")}
                      </td>
                      <td className="p-3.5">
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {log.role || "-"}
                        </span>
                      </td>
                      <td className="p-3.5">
                        <div className="flex items-center gap-1.5 font-semibold text-gray-700">
                          <UserCircle2 className="w-4 h-4 text-emerald-600" />
                          <span>{log.employee?.full_name || log.employee?.name || "-"}</span>
                        </div>
                      </td>
                      <td className="p-3.5 text-center">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => setSelectedLogDetail(log)}
                          className="p-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl font-bold transition-all"
                          title="Lihat Detail Log"
                        >
                          <Eye className="w-4 h-4" />
                        </motion.button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-100 gap-3 text-xs">
          <p className="text-gray-400">
            Menampilkan <span className="font-bold text-gray-700">{logs.length}</span> dari total{" "}
            <span className="font-bold text-gray-700">{totalPages * filters.per_page}</span> log audit
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                page <= 1
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-semibold text-gray-600 px-2">
              Halaman {page} dari {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                page >= totalPages
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200"
              }`}
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* MODAL FILTER */}
      <AnimatePresence>
        {showFilterModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden border border-gray-100 flex flex-col"
            >
              <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-2xl bg-emerald-600 text-white shadow-md shadow-emerald-500/20">
                    <Filter className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Filter Log Aktivitas</h2>
                    <p className="text-xs text-gray-500">Saring berdasarkan karyawan, role, dan tipe log</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowFilterModal(false)}
                  className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white/80 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 text-xs">
                <div className="space-y-1.5">
                  <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider">
                    Pilih Employee / Aktor
                  </label>
                  <select
                    value={filters.employee_id}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, employee_id: e.target.value }))
                    }
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  >
                    <option value="">Semua Karyawan</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.full_name || emp.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider">
                    Role Pengguna
                  </label>
                  <select
                    value={filters.role}
                    onChange={(e) => setFilters((prev) => ({ ...prev, role: e.target.value }))}
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  >
                    <option value="">Semua Role</option>
                    <option value="manager">Manager</option>
                    <option value="employee">Employee / Kasir</option>
                    <option value="owner">Owner</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-gray-700 font-bold uppercase text-[10px] tracking-wider">
                    Tipe Kata Kunci Log
                  </label>
                  <input
                    type="text"
                    placeholder="Contoh: invoice, transaction, PO..."
                    value={filters.type}
                    onChange={(e) => setFilters((prev) => ({ ...prev, type: e.target.value }))}
                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                  />
                </div>
              </div>

              <div className="p-4 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50/50">
                <button
                  onClick={resetFilter}
                  className="px-4 py-2.5 rounded-2xl bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-semibold transition-colors flex items-center gap-1.5"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Reset</span>
                </button>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowFilterModal(false)}
                    className="px-4 py-2.5 rounded-2xl bg-white border border-gray-200 hover:bg-gray-100 text-gray-700 text-xs font-semibold transition-colors"
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleFilterApply}
                    className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-1.5"
                  >
                    <Check className="w-4 h-4" />
                    <span>Terapkan</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL DETAIL LOG */}
      <AnimatePresence>
        {selectedLogDetail && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100 flex flex-col"
            >
              <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-emerald-50 to-teal-50">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-2xl bg-emerald-600 text-white shadow-md shadow-emerald-500/20">
                    <Activity className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-gray-900">Rincian Audit Log #{selectedLogDetail.id}</h2>
                    <p className="text-xs text-gray-500">Jejak rekaman aktivitas sistem</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedLogDetail(null)}
                  className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-white/80 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4 text-xs font-mono">
                <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400 font-bold">Waktu Log:</span>
                    <span className="font-bold text-gray-800">
                      {new Date(selectedLogDetail.log_at || selectedLogDetail.created_at).toLocaleString("id-ID")}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 font-bold">Aktor / Staff:</span>
                    <span className="font-bold text-emerald-700">
                      {selectedLogDetail.employee?.full_name || selectedLogDetail.employee?.name || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 font-bold">Role Aktor:</span>
                    <span className="font-bold text-gray-800 uppercase">{selectedLogDetail.role || "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 font-bold">Kode Referensi:</span>
                    <span className="font-bold text-gray-800">
                      {selectedLogDetail.ref?.code || selectedLogDetail.code || "-"}
                    </span>
                  </div>
                  {selectedLogDetail.obat?.name && (
                    <div className="flex justify-between">
                      <span className="text-gray-400 font-bold">Obat Terkait:</span>
                      <span className="font-bold text-emerald-700">{selectedLogDetail.obat.name}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] font-bold text-gray-400 uppercase font-sans">
                    Pesan Aktivitas Log:
                  </span>
                  <div className="p-3 bg-emerald-50/50 rounded-xl border border-emerald-100 text-emerald-900 font-semibold leading-relaxed">
                    {selectedLogDetail.message || "Tidak ada rincian pesan."}
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-gray-100 flex justify-end bg-gray-50/50">
                <button
                  onClick={() => setSelectedLogDetail(null)}
                  className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-bold shadow-md shadow-emerald-600/20"
                >
                  Tutup Rincian Log
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Logs;
